import React, { useEffect, useRef, useState } from "react";
import { Play, Volume2, VolumeX, X, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Authentic community moments — the truck at events, real diners, Mama serving.
 * Two short-form videos (MP4/H.264 for universal browser support) + three
 * candid photos. Autoplay-in-view (muted + loop), click any tile to open the
 * lightbox for full-screen playback.
 */
const CDN = "https://customer-assets-m6fa6gv7.emergentagent.net/job_soul-food-table/artifacts";

const MEDIA = [
  {
    kind: "video",
    id: "vid1",
    src: "/media/community-1.mp4",
    poster: "/media/community-1-poster.jpg",
    caption: "Mama's truck on the road — a bright afternoon.",
    span: "md:col-span-7 md:row-span-2",   // hero tile
    aspect: "aspect-[3/4]",
  },
  {
    kind: "image",
    id: "img1",
    src: `${CDN}/36dzbbcq_IMG_2194.jpeg`,
    caption: "Community feast night — bunting, prayers, plates.",
    span: "md:col-span-5",
    aspect: "aspect-[4/5]",
  },
  {
    kind: "image",
    id: "img2",
    src: `${CDN}/idyinb16_IMG_2195.jpeg`,
    caption: "Mama's team plating orders for the queue.",
    span: "md:col-span-5",
    aspect: "aspect-[4/5]",
  },
  {
    kind: "video",
    id: "vid2",
    src: "/media/community-2.mp4",
    poster: "/media/community-2-poster.jpg",
    caption: "A quick spin around the setup.",
    span: "md:col-span-4",
    aspect: "aspect-[3/4]",
  },
  {
    kind: "image",
    id: "img3",
    src: `${CDN}/96o3fiq4_IMG_2196.jpeg`,
    caption: "Mama and daughter behind the window.",
    span: "md:col-span-8",
    aspect: "aspect-[16/10]",
  },
];

/* ---------------- Autoplay-in-view video tile ---------------- */
function VideoTile({ item, onOpen }) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && entry.intersectionRatio > 0.4) {
          el.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
        } else {
          el.pause();
          setPlaying(false);
        }
      },
      { threshold: [0, 0.4, 0.75] }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <button
      type="button"
      onClick={() => onOpen(item)}
      data-testid={`community-tile-${item.id}`}
      className={`relative group overflow-hidden ${item.aspect} w-full block bg-black`}
      aria-label={item.caption}
    >
      <video
        ref={ref}
        src={item.src}
        poster={item.poster}
        muted
        loop
        playsInline
        preload="metadata"
        className="absolute inset-0 w-full h-full object-cover transition duration-700 group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
      {!playing && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="rounded-full bg-white/15 backdrop-blur-md p-4 border border-white/25">
            <Play size={22} className="text-white translate-x-0.5" fill="currentColor" />
          </div>
        </div>
      )}
      <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5 pt-12 text-left bg-gradient-to-t from-black/65 via-black/30 to-transparent">
        <div className="text-[10px] tracking-[0.28em] uppercase text-[#E8C899] mb-1">
          {item.kind === "video" ? "Reel" : "Photo"}
        </div>
        <div className="text-sm md:text-base text-white font-light leading-snug drop-shadow">
          {item.caption}
        </div>
      </div>
    </button>
  );
}

/* ---------------- Image tile ---------------- */
function ImageTile({ item, onOpen }) {
  return (
    <button
      type="button"
      onClick={() => onOpen(item)}
      data-testid={`community-tile-${item.id}`}
      className={`relative group overflow-hidden ${item.aspect} w-full block bg-black`}
      aria-label={item.caption}
    >
      <img
        src={item.src}
        alt={item.caption}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover transition duration-700 group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-90 group-hover:opacity-100 transition" />
      <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5 pt-12 text-left bg-gradient-to-t from-black/65 via-black/30 to-transparent">
        <div className="text-[10px] tracking-[0.28em] uppercase text-[#E8C899] mb-1">
          Photo
        </div>
        <div className="text-sm md:text-base text-white font-light leading-snug drop-shadow">
          {item.caption}
        </div>
      </div>
    </button>
  );
}

/* ---------------- Lightbox ---------------- */
function Lightbox({ item, onClose, onPrev, onNext }) {
  // Default muted=true so autoplay works in all browsers; user can unmute
  // via the toggle. Resets to muted on each new item (deliberate).
  const [muted, setMuted] = useState(true);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, onPrev, onNext]);

  if (!item) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4 md:p-10"
      onClick={onClose}
      data-testid="community-lightbox"
    >
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="absolute top-5 right-5 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"
        data-testid="community-lightbox-close"
        aria-label="Close"
      >
        <X size={20} />
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); onPrev(); }}
        className="absolute left-3 md:left-8 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"
        aria-label="Previous"
      >
        <ChevronLeft size={22} />
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); onNext(); }}
        className="absolute right-3 md:right-8 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"
        aria-label="Next"
      >
        <ChevronRight size={22} />
      </button>

      <div
        className="relative max-w-5xl max-h-[85vh] w-full flex flex-col items-center gap-4"
        onClick={(e) => e.stopPropagation()}
      >
        {item.kind === "video" ? (
          <div className="relative w-full max-h-[75vh] flex items-center justify-center">
            <video
              key={item.src}
              src={item.src}
              poster={item.poster}
              autoPlay
              controls
              muted={muted}
              playsInline
              className="max-h-[75vh] max-w-full rounded-md"
            />
            <button
              onClick={() => setMuted((m) => !m)}
              className="absolute bottom-4 right-4 p-2 rounded-full bg-black/60 hover:bg-black/80 text-white"
              aria-label={muted ? "Unmute" : "Mute"}
            >
              {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
            </button>
          </div>
        ) : (
          <img
            src={item.src}
            alt={item.caption}
            className="max-h-[75vh] max-w-full object-contain rounded-md"
          />
        )}
        <div className="text-center text-[var(--mm-cream)] text-sm md:text-base font-light max-w-2xl">
          {item.caption}
        </div>
      </div>
    </div>
  );
}

/* ---------------- Section ---------------- */
export default function Community() {
  const [activeIdx, setActiveIdx] = useState(null);
  const activeItem = activeIdx == null ? null : MEDIA[activeIdx];

  return (
    <section id="community" className="py-24 md:py-36 bg-[var(--mm-bg)]">
      <div className="max-w-[1300px] mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16 fade-in">
          <div>
            <div className="section-label mb-4">Community</div>
            <h2 className="font-display text-5xl md:text-7xl italic font-light text-[var(--mm-cream)] leading-[0.95]">
              Around <br className="hidden md:block" />the truck.
            </h2>
          </div>
          <p className="text-[var(--mm-cream-muted)] max-w-md text-base md:text-lg leading-relaxed font-light mt-6 md:mt-0">
            Real customers, real events, real plates. Mamamina&apos;s Soulfeed on wheels
            — a taste of Liberia wherever Brisbane needs it.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4 fade-in">
          {MEDIA.map((item, idx) => (
            <div key={item.id} className={item.span || "md:col-span-6"}>
              {item.kind === "video" ? (
                <VideoTile item={item} onOpen={() => setActiveIdx(idx)} />
              ) : (
                <ImageTile item={item} onOpen={() => setActiveIdx(idx)} />
              )}
            </div>
          ))}
        </div>
      </div>

      {activeItem && (
        <Lightbox
          item={activeItem}
          onClose={() => setActiveIdx(null)}
          onPrev={() => setActiveIdx((i) => (i - 1 + MEDIA.length) % MEDIA.length)}
          onNext={() => setActiveIdx((i) => (i + 1) % MEDIA.length)}
        />
      )}
    </section>
  );
}
